import React, { useContext, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { NavigationUtils } from 'lib/navigationUtilities';
import { MessageLoading, MessageError, MessageEmpty, MessageToast } from 'components/element/MessageTypes';
import { Operations } from './lib/Operations';
import { useThreadMap, NestedReply } from './NestedReply';
import { AuthContext } from 'components/user/AuthContext';
import BackTitle from 'components/element/BackTitle';
import SortDropdown from './actions/SortDropdown';
import Twut from './Twut';
import api from 'lib/api';

const TwutThread = () => {
  const { navigateToThread, navigateBack, navigateHome, navigateToLocation, locationPathName, locationSearch, urlParams } = NavigationUtils();
  const { twutId } = urlParams;
  const { isLoggedIn } = useContext(AuthContext);
  const queryClient = useQueryClient();

  const urlSearchParamsObj = new URLSearchParams(locationSearch);
  const initialSort = urlSearchParamsObj.get('sort_by') || 'newest';

  const [sortBy, setSortBy] = useState(initialSort);
  const queryKey = ['twutThread', twutId, sortBy];

  const {
    toast, clearToast, isProcessing,
    expandedReplies, handleExpandReplies,
    updateTwut, deleteTwut, handleReplySubmission
  } = Operations(queryKey, {
    threadId: twutId,
    navigateToThread,
    navigateHome
  });

  const {
    data: thread,
    isLoading,
    isError,
    error,
    refetch
  } = useQuery({
    queryKey,
    queryFn: () => api.twut.listThread(twutId, sortBy),
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (thread?.sort_option && thread.sort_option !== sortBy) {
      setSortBy(thread.sort_option);
    }
  }, [thread, sortBy]);

  useEffect(() => {
    refetch();
  }, [isLoggedIn, sortBy, refetch]);

  const { getReplyDepth } = useThreadMap(thread, expandedReplies);

  const handleReply = (parentId, data) => {
    handleReplySubmission(parentId, data, getReplyDepth);
  };

  const handleMainTwutDelete = (id) => {
    queryClient.clear();
    api.twut.delete(id).then(() => navigateHome());
  };

  const handleSortChange = (newSortBy) => {
    if (newSortBy !== sortBy) {
      const newParams = new URLSearchParams(locationSearch);
      newParams.set('sort_by', newSortBy);
      navigateToLocation(`${locationPathName}?${newParams.toString()}`, { replace: true });

      setSortBy(newSortBy);

      const expandedKeys = Object.keys(expandedReplies);
      if (expandedKeys.length > 0) {
        expandedKeys.forEach(key => handleExpandReplies(key));
      }

      queryClient.removeQueries(['twutThread', twutId, sortBy]);
      setTimeout(() => refetch(), 50);
    }
  };

  const handleBackClick = () => {
    navigateBack();
  };

  if (isLoading) return <MessageLoading message="Loading Thread..." />;
  if (isError) return <MessageError message="Error fetching Twuts" error={error} />;

  return (
    <div className="twut-thread">
      <MessageToast toast={toast} onClose={clearToast} />

      {thread && (
        <>
          <div className="border-t border-b border-gray-less mb-3">
            <BackTitle onClick={handleBackClick} hoverText="Back" title="Read Thread" subtitle={`${thread.replies_count + 1} messages`} />
          </div>

          <div className="main-twut mb-4">
            <Twut
              twut={thread}
              onUpdate={updateTwut}
              onDelete={handleMainTwutDelete}
              onReply={handleReply}
              isUpdating={isProcessing}
            />
          </div>

          {thread.replies && thread.replies.length > 0 ? (
            <div className="replies-section">
              <div className="mb-4 pl-2">
                <h3 className="text-lg font-medium text-gray-strong mb-2">
                  Replies ({thread.replies_count})
                </h3>
                <SortDropdown
                  currentSort={thread?.sort_option || sortBy}
                  onSortChange={handleSortChange}
                />
              </div>
              <div>
                <NestedReply
                  replies={thread.replies}
                  expandedReplies={expandedReplies}
                  handleExpandReplies={handleExpandReplies}
                  handleViewThread={navigateToThread}
                  handleUpdateSubmission={updateTwut}
                  handleDeleteTwut={deleteTwut}
                  handleReplySubmission={handleReply}
                  isProcessing={isProcessing}
                  getReplyDepth={getReplyDepth}
                />
              </div>
            </div>
          ) : (
            <MessageEmpty message="No replies yet. Be the first to reply!" />
          )}
        </>
      )}
    </div>
  );
};

export default TwutThread;
